import { useState } from "react";
import { toast } from "sonner";
import { Snowflake, Phone, Building2, User as UserIcon, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhoneInput } from "@/components/PhoneInput";
import { parsePhone } from "@/lib/constants";

export default function OnboardingGate() {
  const { auth, checkAuth } = useAuth();
  const u = auth?.user;
  const [form, setForm] = useState({ name: u?.name || "", phone: "", company: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Please add your contact name.");
      return;
    }
    if (parsePhone(form.phone).local.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      await checkAuth();
      toast.success("Welcome to Reefer Sathi!");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div data-testid="onboarding-gate" className="max-w-md w-full rounded-2xl border border-border bg-card/60 p-8 animate-fadeup">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Snowflake className="w-5 h-5 text-primary" />
          </div>
          <span className="font-heading font-extrabold text-lg">Reefer Sathi</span>
        </div>
        <h1 className="font-heading font-bold text-2xl mb-1">Complete your profile</h1>
        <p className="text-sm text-muted-foreground mb-6">We need a few details so other transporters can reach you on WhatsApp. This takes 20 seconds.</p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><UserIcon className="w-3.5 h-3.5" /> Contact name</Label>
            <Input data-testid="onboarding-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> Phone / WhatsApp number</Label>
            <PhoneInput testid="onboarding-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Company (optional)</Label>
            <Input data-testid="onboarding-company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} placeholder="Your company name" />
          </div>
          <Button data-testid="onboarding-submit" onClick={submit} disabled={saving} className="w-full rounded-full h-11 font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? "Saving…" : <>Continue to app <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
